import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import secureLocalStorage from 'react-secure-storage';
import styles from './Payment.module.css';
import { Button, ButtonHalf } from '../../../theme/button';
import { useAuth } from '../../../contexts/AuthContext';

const stripePromise = loadStripe('pk_live_51Nz01ZCVeN2CV0i6A6Jk9RSHkT4TqNYKoDIcrk1PMDJfnDddPl9cenEMpus6SH8h5iHSpyNp704zR4mJFh4E156x00tqce8Fkf');

function CheckoutForm() {
  const stripe = useStripe();
  const { getValidToken, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    const API_URL = 'https://p4og1sw6yg.execute-api.us-east-1.amazonaws.com/prod/checkout';

    try {
      const validToken = await getValidToken();

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${validToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ platform: 'web' }),
      });

      const data = await response.json();

      // If the API returned a message (e.g., App Store/Play Store), just alert and exit
      if (data.message) {
        alert(data.message);
        return;
      }

      // Save subscriptionMetadata and update state
      if (data.subscriberMetadata) {
        localStorage.setItem('subscriberMetadata', JSON.stringify(data.subscriberMetadata));
        setSubscriptionData(data);
        setIsSubscribed(data.subscriberMetadata.status === 'active');
      }

      // Web: redirect to Stripe Checkout if sessionId exists
      if (data.sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) {
          console.error(error);
          alert(error.message);
        }
      } else if (!data.subscriberMetadata) {
        console.error('No sessionId or subscriberMetadata returned from checkout API.');
        alert('Something went wrong. Please try again.');
      }

    } catch (err) {
      console.error('Checkout error:', err);
      alert('Something went wrong with checkout.');
    } finally {
      setLoading(false);
    }
  };

  const checkActiveCustomerSubscription = async () => {
    const API_URL = "https://p4og1sw6yg.execute-api.us-east-1.amazonaws.com/prod/checkCustomer";

    try {
      const validToken = await getValidToken();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${validToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 404) {
        console.warn("Customer not found. Logging out.");
        logout();
        return null;
      }

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const data = await response.json();

      // Save subscriptionMetadata and trial info
      localStorage.setItem("subscriberMetadata", JSON.stringify(data.subscriberMetadata));
      const trialLeft = data.trialLeft ?? 0;
      localStorage.setItem("trialLeft", String(trialLeft));

      setSubscriptionData({
        ...data,
        trialLeft,
      });
      setIsSubscribed(data.subscriberMetadata.status === 'active');

      return data;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return null;
    }
  };

  useEffect(() => {
    checkActiveCustomerSubscription().finally(() => setLoading(false));
  }, []);

  return (
    <div className={styles.borderedContainer}>
      <div className={styles.billingAmount}>
        <span className={styles.billingDollar}>$24</span>
        <span className={styles.billingMonth}>/ Year</span>
      </div>
      <p className={styles.billingInfo}>Billed Annually ($2/month)</p>

      <Button
        className={styles.paymentPageButton}
        //disabled={isSubscribed || loading} // <--- disable if already subscribed
        onClick={handleCheckout}
        style={{
          opacity: isSubscribed || loading ? 0.5 : 1,
          cursor: isSubscribed || loading ? 'not-allowed' : 'pointer',
        }}
        disabled={isSubscribed || loading}
      >
        {isSubscribed ? "Already Subscribed" : "Subscribe Now"}
      </Button>
    </div>
  );
}

export default function Payment() {
  return (
    <Elements stripe={stripePromise}>
      <div className={styles.container}>
        <div style={{ textAlign: 'center' }}>
          <img
            src={require('../../../assets/loggableLogos/LoggableIconHorizontal.png')}
            alt="Loggable Logo"
            className={styles.loggableLogo}
          />
        </div>

        <h1 className={styles.titleText}>
          Fly High, Log Smart: Transforming Your Handwritten Flight Records
        </h1>

        <p className={styles.descriptionText}>
          Streamline your flight logging with the Loggable app, which converts handwritten records into organized digital formats. Enjoy intuitive features and secure storage so you can focus on what you love—taking to the skies!
        </p>

        <CheckoutForm />

        <div className={styles.featureSection}>
          <h2 className={styles.titleText}>Why Loggable?</h2>

          <div className={styles.featureItem}>
            <h3 className={styles.featureTitle}>Unlimited Text Extraction</h3>
            <p className={styles.featureDescription}>
              Easily convert all your handwritten logs into digital text without limits.
            </p>
          </div>

          <div className={styles.featureItem}>
            <h3 className={styles.featureTitle}>Export to Excel, Google Sheets, and More!</h3>
            <p className={styles.featureDescription}>
              Share and manage your data effortlessly by exporting to popular formats.
            </p>
          </div>

          <div className={styles.featureItem}>
            <h3 className={styles.featureTitle}>Cloud Backup and Sync</h3>
            <p className={styles.featureDescription}>
              Secure your data with automatic cloud backups and access your logs from any device.
            </p>
          </div>

          <div className={styles.featureItem}>
            <h3 className={styles.featureTitle}>Intuitive User Interface</h3>
            <p className={styles.featureDescription}>
              Enjoy a user-friendly design that makes logging and accessing your flights simple.
            </p>
          </div>
        </div>
      </div>
    </Elements>
  );
}
